import { Injectable } from '@angular/core';

import { CreditosService } from '../../../../modules/finanzas/creditos/creditos.service';
import {
  CobroCuotasCreditosModel,
  ConstitucionDelCreditoCreditosModel,
  CreditosModel,
  DeudaACortoPlazoCreditosModel,
} from '../../../../modules/finanzas/creditos/creditos.model';
import { CreditosConEmpleadosService } from '../../../../modules/finanzas/creditos-con-empleados/creditos-con-empleados.service';
import {
  CobroCuotasCreditosConEmpleadosModel,
  ConstitucionDelCreditoCreditosConEmpleadosModel,
  CreditosConEmpleadosModel,
  DeudaACortoPlazoCreditosConEmpleadosModel,
} from '../../../../modules/finanzas/creditos-con-empleados/creditos-con-empleados.model';
import { consts } from '../../../../consts';
import { CreditKind } from '../models/credit-shared.model';

@Injectable({ providedIn: 'root' })
export class CreditOperationsService {

  constructor(
    private creditosService: CreditosService,
    private employeesService: CreditosConEmpleadosService,
  ) { }

  parentKey(kind: CreditKind): 'creditosId' | 'creditosConEmpleadosId' {
    return kind === 'employee' ? 'creditosConEmpleadosId' : 'creditosId';
  }

  list(kind: CreditKind): Promise<any[]> {
    const request = kind === 'employee'
      ? this.employeesService.list(1, 1000)
      : this.creditosService.list(1, 1000);
    return request.then(data => (data && Array.isArray(data.list) ? data.list : []));
  }

  get(kind: CreditKind, uid: string): Promise<any> {
    return kind === 'employee' ? this.employeesService.get(uid) : this.creditosService.get(uid);
  }

  deleteCredit(kind: CreditKind, uid: string): Promise<void> {
    const request = kind === 'employee' ? this.employeesService.delete(uid) : this.creditosService.delete(uid);
    return request.then(ok => this.ensureOk(ok));
  }

  downloadAttachment(kind: CreditKind, uid: string): Promise<Blob> {
    return kind === 'employee'
      ? this.employeesService.downloadFileAttachment(uid)
      : this.creditosService.downloadFileAttachment(uid);
  }

  attachFile(kind: CreditKind, uid: string, fileUid: string): Promise<void> {
    if (kind === 'employee') {
      const payload: CreditosConEmpleadosModel = {
        carencia: 0,
        bankAccountsUid: consts.EMPTY_GUID,
        employeesId: 0,
        employeesName: '',
        importeInicial: 0,
        importePendiente: 0,
        fechaConstitucion: new Date(),
        fechaFinalizacion: new Date(),
        plazo: 0,
        tipoInteres: 0,
        uid,
        tipoLiquidaciones: 0,
        filesCreditosConEmpleadosUid: fileUid,
      };
      return this.employeesService.editFileAttachment(payload).then(ok => this.ensureOk(ok));
    }

    const payload: CreditosModel = {
      carencia: 0,
      bankAccountsUid: consts.EMPTY_GUID,
      accountingModelsCortoPlazoNumber: '',
      accountingModelsLargoPlazoNumber: '',
      cuentaContableACrear: '',
      descripcion: '',
      importeInicial: 0,
      importePendiente: 0,
      fechaConstitucion: new Date(),
      fechaFinalizacion: new Date(),
      plazo: 0,
      tipoInteres: 0,
      uid,
      tipoLiquidaciones: 0,
      filesCreditosUid: fileUid,
      duracion: 0,
    };
    return this.creditosService.editFileAttachment(payload).then(ok => this.ensureOk(ok));
  }

  newConstitution(kind: CreditKind): any {
    return kind === 'employee'
      ? new ConstitucionDelCreditoCreditosConEmpleadosModel()
      : new ConstitucionDelCreditoCreditosModel();
  }

  listConstitutions(kind: CreditKind, uid: string): Promise<any[]> {
    if (kind === 'employee') {
      return Promise.all([
        this.employeesService.constitucionDelCreditoList(uid, 1),
        this.employeesService.constitucionDelCreditoList(uid, 2),
      ]).then(([shortTerm, longTerm]) => [...this.asArray(shortTerm), ...this.asArray(longTerm)]);
    }
    return this.creditosService.constitucionDelCreditoList(uid).then(list => this.asArray(list));
  }

  saveConstitution(kind: CreditKind, payload: any, isEdit: boolean): Promise<boolean> {
    if (kind === 'employee') {
      return isEdit
        ? this.employeesService.constitucionDelCreditoUpdate(payload)
        : this.employeesService.constitucionDelCreditoCreate(payload);
    }
    return isEdit
      ? this.creditosService.constitucionDelCreditoUpdate(payload)
      : this.creditosService.constitucionDelCreditoCreate(payload);
  }

  deleteConstitution(kind: CreditKind, uid: string): Promise<boolean> {
    return kind === 'employee'
      ? this.employeesService.constitucionDelCreditoDelete(uid)
      : this.creditosService.constitucionDelCreditoDelete(uid);
  }

  newShortTermDebt(kind: CreditKind): any {
    return kind === 'employee'
      ? new DeudaACortoPlazoCreditosConEmpleadosModel()
      : new DeudaACortoPlazoCreditosModel();
  }

  listShortTermDebts(kind: CreditKind, uid: string): Promise<any[]> {
    const request = kind === 'employee'
      ? this.employeesService.deudaACortoPlazoList(uid)
      : this.creditosService.deudaACortoPlazoList(uid);
    return request.then(list => this.asArray(list));
  }

  saveShortTermDebt(kind: CreditKind, payload: any, isEdit: boolean): Promise<boolean> {
    if (kind === 'employee') {
      return isEdit
        ? this.employeesService.deudaACortoPlazoUpdate(payload)
        : this.employeesService.deudaACortoPlazoCreate(payload);
    }
    return isEdit
      ? this.creditosService.deudaACortoPlazoUpdate(payload)
      : this.creditosService.deudaACortoPlazoCreate(payload);
  }

  deleteShortTermDebt(kind: CreditKind, uid: string): Promise<boolean> {
    return kind === 'employee'
      ? this.employeesService.deudaACortoPlazoDelete(uid)
      : this.creditosService.deudaACortoPlazoDelete(uid);
  }

  newInstallment(kind: CreditKind): any {
    return kind === 'employee'
      ? new CobroCuotasCreditosConEmpleadosModel()
      : new CobroCuotasCreditosModel();
  }

  listInstallments(kind: CreditKind, uid: string): Promise<any[]> {
    const request = kind === 'employee'
      ? this.employeesService.cobroCoutasList(uid)
      : this.creditosService.cobroCoutasList(uid);
    return request.then(list => this.asArray(list));
  }

  saveInstallment(kind: CreditKind, payload: any, isEdit: boolean): Promise<boolean> {
    if (kind === 'employee') {
      return isEdit
        ? this.employeesService.cobroCoutasUpdate(payload)
        : this.employeesService.cobroCoutasCreate(payload);
    }
    return isEdit
      ? this.creditosService.cobroCoutasUpdate(payload)
      : this.creditosService.cobroCoutasCreate(payload);
  }

  deleteInstallment(kind: CreditKind, uid: string): Promise<boolean> {
    return kind === 'employee'
      ? this.employeesService.cobroCoutasDelete(uid)
      : this.creditosService.cobroCoutasDelete(uid);
  }

  private asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }

  private ensureOk(ok: unknown): void {
    if (ok !== true) throw new Error();
  }
}
